import {
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api, call, setCsrfToken, upload } from './client';
import type {
  AdvisoryText,
  ApiToken,
  Asset,
  AssetDetail,
  AuditPage,
  Client,
  EolNotice,
  EolPromptVersion,
  RemediationVersion,
  Deployment,
  ProjectStackItem,
  Stack,
  StackVersions,
  Finding,
  FindingDetail,
  FindingSummary,
  CombinedPrompt,
  CreatedApiToken,
  DeletionPreview,
  Assignment,
  Dashboard,
  DeliveryPage,
  FindingPage,
  GroupChannel,
  GroupLink,
  LineAccountLink,
  MyChannels,
  NotificationPreferences,
  NotificationSettings,
  PreferencesInput,
  Routes,
  TelegramLink,
  UnsubscribeInfo,
  Unsubscribed,
  WorkspaceDefaultsInput,
  FixPrompt,
  BulkResult,
  Invitation,
  List,
  Locale,
  LoginResult,
  McpSetup,
  Me,
  Member,
  NewAssetKind,
  Project,
  Session,
  SlaPolicy,
  Snapshot,
  SnapshotDiff,
  SnapshotSummary,
  Workspace,
  AiUsage,
  BillingDetailsInput,
  BillingOrder,
  BillingOrderStatus,
  BillingOverview,
  BillingPayments,
  ExportInput,
  FalsePositiveReport,
  FalsePositiveState,
  MitigationReviewDetail,
  MitigationReviewQueue,
  FeatureFlag,
  ServiceSwitches,
  GithubAvailableRepo,
  GithubDiscovery,
  GithubInstallations,
  GithubRepository,
  PlanCatalog,
  PlanOverrideInput,
  Report,
  ReportInput,
  StaffAlert,
  StaffFeedback,
  StaffFeedbackFilter,
  StaffFeedbackList,
  CreateFeedback,
  FeedbackKind,
  FeedbackStatus,
  MyFeedback,
  StaffGrant,
  StaffPayment,
  StaffRefund,
  StaffRole,
  StaffUser,
  StaffWorkspace,
  StaffWorkspaceDetail,
  SyncHealth,
  UpgradeQuote,
  WorkspacePlan,
  AdvisoryOverride,
  AdvisoryOverrideList,
} from './types';
import type { UploadFile } from '../lib/files';

/* ------------------------------------------------------------------ keys */

export const qk = {
  session: ['session'] as const,
  me: ['me'] as const,
  providers: ['oauth-providers'] as const,
  serviceStatus: ['service-status'] as const,
  workspaces: ['workspaces'] as const,
  ws: (ws: string) => ['ws', ws] as const,
  members: (ws: string) => ['ws', ws, 'members'] as const,
  invitations: (ws: string) => ['ws', ws, 'invitations'] as const,
  clients: (ws: string) => ['ws', ws, 'clients'] as const,
  client: (ws: string, id: string) => ['ws', ws, 'clients', id] as const,
  projects: (ws: string) => ['ws', ws, 'projects'] as const,
  project: (ws: string, id: string) => ['ws', ws, 'project', id] as const,
  assets: (ws: string, projectId: string) => ['ws', ws, 'project', projectId, 'assets'] as const,
  deployments: (ws: string, projectId: string) =>
    ['ws', ws, 'project', projectId, 'deployments'] as const,
  projectStack: (ws: string, projectId: string) =>
    ['ws', ws, 'project', projectId, 'stack'] as const,
  asset: (ws: string, id: string) => ['ws', ws, 'asset', id] as const,
  snapshots: (ws: string, assetId: string) => ['ws', ws, 'asset', assetId, 'snapshots'] as const,
  snapshot: (ws: string, id: string) => ['ws', ws, 'snapshot', id] as const,
  diff: (ws: string, assetId: string, from: string, to: string) =>
    ['ws', ws, 'asset', assetId, 'diff', from, to] as const,
  findings: (ws: string) => ['ws', ws, 'findings'] as const,
  advisoryOverrides: (ws: string) => ['ws', ws, 'advisory-overrides'] as const,
  findingList: (ws: string, filters: FindingFilters) =>
    ['ws', ws, 'findings', 'list', filters] as const,
  summary: (ws: string, scope: SummaryScope) => ['ws', ws, 'findings', 'summary', scope] as const,
  finding: (ws: string, id: string, lang: Locale) =>
    ['ws', ws, 'findings', 'detail', id, lang] as const,
  fixPrompt: (ws: string, id: string, lang: Locale) =>
    ['ws', ws, 'findings', 'prompt', id, lang] as const,
  eol: (ws: string, projectId?: string) => ['ws', ws, 'eol', projectId ?? 'all'] as const,
  eolPrompts: (ws: string) => ['ws', ws, 'eol-prompts'] as const,
  sla: (ws: string) => ['ws', ws, 'sla'] as const,
  tokens: (ws: string) => ['ws', ws, 'api-tokens'] as const,
  dashboard: (ws: string, scope: DashboardScope) =>
    ['ws', ws, 'findings', 'dashboard', scope] as const,
  assignments: (ws: string, kind: AssignmentTarget, id: string) =>
    ['ws', ws, 'assignments', kind, id] as const,
  myChannels: ['me', 'notification-channels'] as const,
  notifications: (ws: string) => ['ws', ws, 'notifications'] as const,
  audit: (ws: string) => ['ws', ws, 'audit'] as const,
  plans: ['billing-plans'] as const,
  billing: ['account', 'billing'] as const,
  workspacePlan: (ws: string) => ['ws', ws, 'plan'] as const,
  reports: (ws: string) => ['ws', ws, 'reports'] as const,
  github: (ws: string) => ['ws', ws, 'github'] as const,
  staff: ['staff'] as const,
  myFeedback: ['me', 'feedback'] as const,
};

const wsPath = (workspaceId: string) => ({ path: { workspaceId } });

/* --------------------------------------------------------------- session */

export function useSession() {
  return useQuery({
    queryKey: qk.session,
    queryFn: async () => {
      const s = await call<Session>(api.GET('/api/v1/auth/session'));
      setCsrfToken('csrfToken' in s ? s.csrfToken : null);
      return s;
    },
    staleTime: 60_000,
  });
}

export function useMe(enabled = true) {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => call<Me>(api.GET('/api/v1/me')),
    enabled,
    staleTime: 60_000,
  });
}

export function useOAuthProviders() {
  return useQuery({
    queryKey: qk.providers,
    queryFn: () => call<{ providers: string[] }>(api.GET('/api/v1/auth/oauth/providers')),
    staleTime: 5 * 60_000,
  });
}

/** FR-ADM-06: sign-up and purchase switches (public). */
export function useServiceStatus() {
  return useQuery({
    queryKey: qk.serviceStatus,
    queryFn: () => call<ServiceSwitches>(api.GET('/api/v1/service-status')),
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      call<LoginResult>(api.POST('/api/v1/auth/login', { body })),
    onSuccess: async (res) => {
      setCsrfToken(res.csrfToken);
      await qc.invalidateQueries({ queryKey: qk.session });
      if (!res.mfaRequired) await qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useSecondFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code?: string; recoveryCode?: string }) =>
      call<LoginResult>(api.POST('/api/v1/auth/login/2fa', { body })),
    onSuccess: async (res) => {
      setCsrfToken(res.csrfToken);
      await qc.invalidateQueries({ queryKey: qk.session });
      await qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => call<unknown>(api.POST('/api/v1/auth/logout')),
    onSettled: () => {
      setCsrfToken(null);
      qc.clear();
    },
  });
}

export function useSignUp() {
  return useMutation({
    mutationFn: (body: {
      email: string;
      password: string;
      displayName?: string;
      locale?: Locale;
    }) => call<{ status: string }>(api.POST('/api/v1/auth/signup', { body })),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) =>
      call<unknown>(api.POST('/api/v1/auth/verify-email', { body: { token } })),
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) =>
      call<unknown>(api.POST('/api/v1/auth/verify-email/resend', { body: { email } })),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      call<unknown>(api.POST('/api/v1/auth/password/forgot', { body: { email } })),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      call<unknown>(api.POST('/api/v1/auth/password/reset', { body })),
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { displayName?: string | null; locale?: Locale }) =>
      call<unknown>(api.PATCH('/api/v1/me', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

/** FR-ACC-09: change the password while signed in (other sessions are signed out). */
export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      call<unknown>(api.POST('/api/v1/auth/password/change', { body })),
  });
}

/** FR-ACC-11: what deleting the account needs and does to each workspace. */
export function useDeletionPreview(enabled: boolean) {
  return useQuery({
    queryKey: [...qk.me, 'deletion-preview'] as const,
    queryFn: () => call<DeletionPreview>(api.GET('/api/v1/me/deletion-preview')),
    enabled,
    staleTime: 0,
  });
}

/** FR-ACC-11: delete the account; the session ends with it. */
export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { password?: string; totpCode?: string; confirmEmail: string }) =>
      call<unknown>(api.DELETE('/api/v1/me', { body })),
    onSuccess: () => {
      setCsrfToken(null);
      qc.clear();
    },
  });
}

export function useTotpSetup() {
  return useMutation({
    mutationFn: () =>
      call<{ secret: string; otpauthUrl?: string; uri?: string }>(
        api.POST('/api/v1/auth/2fa/totp/setup'),
      ),
  });
}

export function useTotpEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      call<{ recoveryCodes: string[] }>(
        api.POST('/api/v1/auth/2fa/totp/enable', { body: { code } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

export function useTotpDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code?: string; recoveryCode?: string }) =>
      call<unknown>(api.POST('/api/v1/auth/2fa/totp/disable', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

export function useRegenerateRecoveryCodes() {
  return useMutation({
    mutationFn: (code: string) =>
      call<{ recoveryCodes: string[] }>(
        api.POST('/api/v1/auth/2fa/recovery-codes', { body: { code } }),
      ),
  });
}

/* ------------------------------------------------------------ workspaces */

export function useWorkspaces(enabled = true) {
  return useQuery({
    queryKey: qk.workspaces,
    queryFn: () => call<List<Workspace>>(api.GET('/api/v1/workspaces')),
    enabled,
  });
}

export function useWorkspace(ws: string, enabled = true) {
  return useQuery({
    queryKey: qk.ws(ws),
    queryFn: () =>
      call<Workspace>(api.GET('/api/v1/workspaces/{workspaceId}', { params: wsPath(ws) })),
    enabled: enabled && Boolean(ws),
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; timeZone?: string }) =>
      call<Workspace>(api.POST('/api/v1/workspaces', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workspaces }),
  });
}

export function useSwitchWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) =>
      call<unknown>(api.PUT('/api/v1/me/current-workspace', { body: { workspaceId } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

export function useUpdateWorkspace(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; timeZone?: string; enforceTwoFactor?: boolean }) =>
      call<Workspace>(api.PATCH('/api/v1/workspaces/{workspaceId}', { params: wsPath(ws), body })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.ws(ws) });
      await qc.invalidateQueries({ queryKey: qk.workspaces });
    },
  });
}

/** Owner: schedule workspace deletion (purged within 30 days, NFR-PRV-03). */
export function useDeleteWorkspace(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason?: string }) =>
      call<{ status: string; purgeAfter: string }>(
        api.DELETE('/api/v1/workspaces/{workspaceId}', { params: wsPath(ws), body }),
      ),
    onSuccess: async () => {
      qc.removeQueries({ queryKey: qk.ws(ws) });
      await qc.invalidateQueries({ queryKey: qk.workspaces });
      await qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useMembers(ws: string) {
  return useQuery({
    queryKey: qk.members(ws),
    queryFn: () =>
      call<List<Member>>(
        api.GET('/api/v1/workspaces/{workspaceId}/members', { params: wsPath(ws) }),
      ),
  });
}

export function useChangeRole(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, role }: { membershipId: string; role: Member['role'] }) =>
      call<Member>(
        api.PATCH('/api/v1/workspaces/{workspaceId}/members/{membershipId}', {
          params: { path: { workspaceId: ws, membershipId } },
          body: { role },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(ws) }),
  });
}

export function useRemoveMember(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/members/{membershipId}', {
          params: { path: { workspaceId: ws, membershipId } },
          body: {},
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(ws) }),
  });
}

export function useInvitations(ws: string, enabled = true) {
  return useQuery({
    queryKey: qk.invitations(ws),
    queryFn: () =>
      call<List<Invitation>>(
        api.GET('/api/v1/workspaces/{workspaceId}/invitations', { params: wsPath(ws) }),
      ),
    enabled,
  });
}

export function useInvite(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: Member['role'] }) =>
      call<Invitation>(
        api.POST('/api/v1/workspaces/{workspaceId}/invitations', { params: wsPath(ws), body }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.invitations(ws) }),
  });
}

export function useRevokeInvitation(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/invitations/{invitationId}', {
          params: { path: { workspaceId: ws, invitationId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.invitations(ws) }),
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      call<{ workspaceId: string; membershipId: string }>(
        api.POST('/api/v1/invitations/accept', { body: { token } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workspaces }),
  });
}

/* --------------------------------------------------- clients and projects */

export function useClients(ws: string, includeArchived = false) {
  return useQuery({
    queryKey: [...qk.clients(ws), includeArchived],
    queryFn: () =>
      call<List<Client>>(
        api.GET('/api/v1/workspaces/{workspaceId}/clients', {
          params: {
            path: { workspaceId: ws },
            query: { includeArchived: String(includeArchived) as 'true' },
          },
        }),
      ),
  });
}

export function useCreateClient(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      call<Client>(
        api.POST('/api/v1/workspaces/{workspaceId}/clients', {
          params: wsPath(ws),
          body: { name },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.clients(ws) }),
  });
}

export function useUpdateClient(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, ...body }: { clientId: string; name?: string; archived?: boolean }) =>
      call<Client>(
        api.PATCH('/api/v1/workspaces/{workspaceId}/clients/{clientId}', {
          params: { path: { workspaceId: ws, clientId } },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.clients(ws) }),
  });
}

/** FR-PRJ-01: only a client without projects can be deleted (`CLIENT_NOT_EMPTY`). */
export function useDeleteClient(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, reason }: { clientId: string; reason?: string }) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/clients/{clientId}', {
          params: { path: { workspaceId: ws, clientId } },
          body: reason ? { reason } : {},
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.clients(ws) }),
  });
}

export function useProjects(
  ws: string,
  opts: { clientId?: string; includeArchived?: boolean } = {},
) {
  return useQuery({
    queryKey: [...qk.projects(ws), opts],
    queryFn: () =>
      call<List<Project>>(
        api.GET('/api/v1/workspaces/{workspaceId}/projects', {
          params: {
            path: { workspaceId: ws },
            query: {
              ...(opts.clientId ? { clientId: opts.clientId } : {}),
              includeArchived: String(opts.includeArchived ?? false) as 'true',
            },
          },
        }),
      ),
  });
}

export function useProject(ws: string, projectId: string) {
  return useQuery({
    queryKey: qk.project(ws, projectId),
    enabled: projectId !== '',
    queryFn: () =>
      call<Project>(
        api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}', {
          params: { path: { workspaceId: ws, projectId } },
        }),
      ),
  });
}

export interface ProjectInput {
  name?: string;
  description?: string | null;
  clientId?: string | null;
  environment?: Project['environment'];
  /** FR-PRJ-04: each list given replaces the stored one. */
  stack?: {
    runtimes?: { name: string; version: string }[];
    frameworks?: { name: string; version: string }[];
    os?: { name: string; release: string }[];
    databases?: { name: string; version: string }[];
    services?: { name: string; version: string }[];
  };
  archived?: boolean;
}

export function useCreateProject(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectInput & { name: string }) =>
      call<Project>(
        api.POST('/api/v1/workspaces/{workspaceId}/projects', {
          params: wsPath(ws),
          body: body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.projects(ws) }),
  });
}

export function useUpdateProject(ws: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectInput) =>
      call<Project>(
        api.PATCH('/api/v1/workspaces/{workspaceId}/projects/{projectId}', {
          params: { path: { workspaceId: ws, projectId } },
          body: body as never,
        }),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.project(ws, projectId) });
      await qc.invalidateQueries({ queryKey: qk.projects(ws) });
      await qc.invalidateQueries({ queryKey: ['ws', ws, 'eol'] });
    },
  });
}

export function useAssets(ws: string, projectId: string, includeArchived = false) {
  return useQuery({
    queryKey: [...qk.assets(ws, projectId), { includeArchived }],
    enabled: projectId !== '',
    queryFn: () =>
      call<List<Asset>>(
        api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}/assets', {
          params: {
            path: { workspaceId: ws, projectId },
            query: { includeArchived: includeArchived ? 'true' : 'false' },
          },
        }),
      ),
  });
}

export function useAsset(ws: string, assetId: string) {
  return useQuery({
    queryKey: qk.asset(ws, assetId),
    enabled: assetId !== '',
    queryFn: () =>
      call<AssetDetail>(
        api.GET('/api/v1/workspaces/{workspaceId}/assets/{assetId}', {
          params: { path: { workspaceId: ws, assetId } },
        }),
      ),
  });
}

/** Deletes the project with its assets and revokes their API tokens. */
export function useDeleteProject(ws: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason?: string }) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/projects/{projectId}', {
          params: { path: { workspaceId: ws, projectId } },
          body,
        }),
      ),
    onSuccess: async () => {
      qc.removeQueries({ queryKey: qk.project(ws, projectId) });
      await qc.invalidateQueries({ queryKey: qk.projects(ws) });
      await qc.invalidateQueries({ queryKey: qk.clients(ws) });
    },
  });
}

export function useCreateAsset(ws: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      kind = 'manifest_upload',
      repositoryUrl,
      addresses,
      database,
      serverAssetId,
    }: {
      name: string;
      kind?: NewAssetKind;
      repositoryUrl?: string;
      addresses?: string[];
      database?: { name: string; version: string };
      /** FR-SRV-01: it runs on this server. */
      serverAssetId?: string;
    }) => {
      const asset = await call<Asset>(
        api.POST('/api/v1/workspaces/{workspaceId}/projects/{projectId}/assets', {
          params: { path: { workspaceId: ws, projectId } },
          body: {
            name,
            kind,
            ...(repositoryUrl ? { repositoryUrl } : {}),
            ...(addresses?.length ? { addresses } : {}),
            ...(database ? { stack: { databases: [database] } as never } : {}),
          },
        }),
      );
      if (serverAssetId) {
        await call(
          api.PUT('/api/v1/workspaces/{workspaceId}/assets/{assetId}/deployments/{serverAssetId}', {
            params: { path: { workspaceId: ws, assetId: asset.id, serverAssetId } },
            body: { ports: [], domains: [] },
          }),
        );
      }
      return asset;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.assets(ws, projectId) });
      await qc.invalidateQueries({ queryKey: qk.deployments(ws, projectId) });
      await qc.invalidateQueries({ queryKey: qk.projectStack(ws, projectId) });
    },
  });
}

export function useUpdateAsset(ws: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name?: string;
      archived?: boolean;
      repositoryUrl?: string | null;
      privateRegistryLookup?: 'allowed' | 'denied';
      addresses?: string[];
      stack?: Partial<Stack>;
      baseImage?: string | null;
    }) =>
      call<Asset>(
        api.PATCH('/api/v1/workspaces/{workspaceId}/assets/{assetId}', {
          params: { path: { workspaceId: ws, assetId } },
          body: body as never,
        }),
      ),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: qk.asset(ws, assetId) });
      await qc.invalidateQueries({ queryKey: qk.assets(ws, a.projectId) });
      await qc.invalidateQueries({ queryKey: qk.projectStack(ws, a.projectId) });
    },
  });
}

/** Deletes the asset and revokes its asset API tokens. */
export function useDeleteAsset(ws: string, assetId: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason?: string }) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/assets/{assetId}', {
          params: { path: { workspaceId: ws, assetId } },
          body,
        }),
      ),
    onSuccess: async () => {
      qc.removeQueries({ queryKey: qk.asset(ws, assetId) });
      await qc.invalidateQueries({ queryKey: qk.assets(ws, projectId) });
    },
  });
}

/* --------------------------------------------------------------- uploads */

export function useSnapshots(ws: string, assetId: string) {
  return useQuery({
    queryKey: qk.snapshots(ws, assetId),
    queryFn: () =>
      call<List<SnapshotSummary>>(
        api.GET('/api/v1/workspaces/{workspaceId}/assets/{assetId}/snapshots', {
          params: { path: { workspaceId: ws, assetId } },
        }),
      ),
    // Poll while matching runs.
    refetchInterval: (q) =>
      q.state.data?.items.some((s) => s.match.status === 'pending') ? 1500 : false,
  });
}

export function useSnapshot(ws: string, snapshotId: string | null) {
  return useQuery({
    queryKey: qk.snapshot(ws, snapshotId ?? ''),
    queryFn: () =>
      call<Snapshot>(
        api.GET('/api/v1/workspaces/{workspaceId}/snapshots/{snapshotId}', {
          params: { path: { workspaceId: ws, snapshotId: snapshotId ?? '' } },
        }),
      ),
    enabled: Boolean(snapshotId),
    refetchInterval: (q) => (q.state.data?.match.status === 'pending' ? 1500 : false),
  });
}

export function useSnapshotDiff(
  ws: string,
  assetId: string,
  from: string | null,
  to: string | null,
) {
  return useQuery({
    queryKey: qk.diff(ws, assetId, from ?? '', to ?? ''),
    queryFn: () =>
      call<SnapshotDiff>(
        api.GET('/api/v1/workspaces/{workspaceId}/assets/{assetId}/snapshots/diff', {
          params: { path: { workspaceId: ws, assetId }, query: { from: from ?? '', to: to ?? '' } },
        }),
      ),
    enabled: Boolean(from && to && from !== to),
  });
}

export function useUploadSnapshot(ws: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (files: UploadFile[]) => {
      const form = new FormData();
      for (const f of files) form.append('files', f.file, f.path.split('/').pop() ?? f.file.name);
      for (const f of files) form.append('paths', f.path);
      return upload<Snapshot>(
        `/api/v1/workspaces/${encodeURIComponent(ws)}/assets/${encodeURIComponent(assetId)}/snapshots`,
        form,
      );
    },
    onSuccess: async (s) => {
      qc.setQueryData(qk.snapshot(ws, s.id), s);
      await qc.invalidateQueries({ queryKey: qk.snapshots(ws, assetId) });
      await qc.invalidateQueries({ queryKey: qk.asset(ws, assetId) });
      await qc.invalidateQueries({ queryKey: qk.findings(ws) });
      // The inventory changes the stack of the asset's project, which this hook does not know.
      await qc.invalidateQueries({
        predicate: ({ queryKey: k }) => k[1] === ws && k[2] === 'project' && k[4] === 'stack',
      });
    },
  });
}

/* -------------------------------------------------------------- findings */

export interface FindingFilters {
  clientId?: string;
  projectId?: string;
  assetId?: string;
  priority?: string;
  status?: string;
  ecosystem?: string;
  scope?: string;
  kev?: 'true' | 'false';
  assignee?: string;
  slaState?: string;
  fixState?: string;
  package?: string;
  advisory?: string;
}

export type FindingSort = 'priority' | 'fixed_at';

export interface SummaryScope {
  clientId?: string;
  projectId?: string;
  assetId?: string;
}

/** Findings, a page at a time; the first page also carries the `total` (FR-FND-01). */
export function useFindings(
  ws: string,
  filters: FindingFilters & { sort?: FindingSort },
  limit = 50,
) {
  return useInfiniteQuery({
    queryKey: qk.findingList(ws, filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call<FindingPage>(
        api.GET('/api/v1/workspaces/{workspaceId}/findings', {
          params: {
            path: { workspaceId: ws },
            query: {
              ...filters,
              limit,
              ...(pageParam ? { cursor: pageParam } : { includeTotal: 'true' }),
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export interface DashboardScope {
  clientId?: string;
  projectId?: string;
}

/** Dashboard (FR-RPT-01): open by priority, SLA counts, 90-day trend, top projects, recent fixes. */
export function useDashboard(ws: string, scope: DashboardScope = {}) {
  return useQuery({
    queryKey: qk.dashboard(ws, scope),
    queryFn: () =>
      call<Dashboard>(
        api.GET('/api/v1/workspaces/{workspaceId}/dashboard', {
          params: { path: { workspaceId: ws }, query: scope },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useFindingSummary(ws: string, scope: SummaryScope = {}) {
  return useQuery({
    queryKey: qk.summary(ws, scope),
    queryFn: () =>
      call<FindingSummary>(
        api.GET('/api/v1/workspaces/{workspaceId}/findings/summary', {
          params: { path: { workspaceId: ws }, query: scope },
        }),
      ),
  });
}

export function useFinding(ws: string, findingId: string, language: Locale) {
  return useQuery({
    queryKey: qk.finding(ws, findingId, language),
    queryFn: () =>
      call<FindingDetail>(
        api.GET('/api/v1/workspaces/{workspaceId}/findings/{findingId}', {
          params: { path: { workspaceId: ws, findingId }, query: { language } },
        }),
      ),
    // Poll while a generation someone asked for is running.
    refetchInterval: (q) => (q.state.data?.remediation.ai.status === 'queued' ? 5000 : false),
  });
}

/** Text of one advisory, for those beyond the 20 most urgent the detail carries; on demand. */
export function useAdvisoryText(ws: string, findingId: string, groupId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...qk.finding(ws, findingId, 'en'), 'advisory-text', groupId],
    queryFn: () =>
      call<AdvisoryText>(
        api.GET('/api/v1/workspaces/{workspaceId}/findings/{findingId}/advisories/{groupId}/text', {
          params: { path: { workspaceId: ws, findingId, groupId } },
        }),
      ),
    enabled,
    staleTime: 300_000,
  });
}

function useInvalidateFindings(ws: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: qk.findings(ws) });
}

export type StatusChange =
  | { status: 'open' | 'in_progress' | 'pending_verification'; note?: string }
  | {
      status: 'ignored';
      reason: 'not_affected' | 'accepted_risk' | 'false_positive';
      note?: string;
      expiresAt?: string;
    }
  | { status: 'snoozed'; until: string; note?: string };

export function useChangeStatus(ws: string, findingId: string) {
  const invalidate = useInvalidateFindings(ws);
  return useMutation({
    mutationFn: (body: StatusChange) =>
      call<Finding>(
        api.POST('/api/v1/workspaces/{workspaceId}/findings/{findingId}/status', {
          params: { path: { workspaceId: ws, findingId } },
          body: body as never,
        }),
      ),
    onSuccess: invalidate,
  });
}

export type AppliedMitigation = { ref: string; note?: string } | { kind: 'other'; note: string };

export function useApplyMitigations(ws: string, findingId: string) {
  const invalidate = useInvalidateFindings(ws);
  return useMutation({
    mutationFn: (body: { mitigations: AppliedMitigation[]; reviewAt?: string; note?: string }) =>
      call<Finding>(
        api.POST('/api/v1/workspaces/{workspaceId}/findings/{findingId}/mitigations', {
          params: { path: { workspaceId: ws, findingId } },
          body: body as never,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useAssign(ws: string, findingId: string) {
  const invalidate = useInvalidateFindings(ws);
  return useMutation({
    mutationFn: (membershipId: string | null) =>
      call<Finding>(
        api.PUT('/api/v1/workspaces/{workspaceId}/findings/{findingId}/assignee', {
          params: { path: { workspaceId: ws, findingId } },
          body: { membershipId } as never,
        }),
      ),
    onSuccess: invalidate,
  });
}

export type BulkAction =
  | { action: 'assign'; findingIds: string[]; membershipId: string | null }
  | { action: 'snooze'; findingIds: string[]; until: string; note?: string }
  | {
      action: 'ignore';
      findingIds: string[];
      reason: 'not_affected' | 'accepted_risk' | 'false_positive';
      note?: string;
      expiresAt?: string;
    };

export function useBulk(ws: string) {
  const invalidate = useInvalidateFindings(ws);
  return useMutation({
    mutationFn: (body: BulkAction) =>
      call<BulkResult>(
        api.POST('/api/v1/workspaces/{workspaceId}/findings/bulk', {
          params: wsPath(ws),
          body: body as never,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useFalsePositive(ws: string, findingId: string) {
  return useMutation({
    mutationFn: (body: { groupIds?: string[]; note?: string }) =>
      call<{ id: string; state: string }>(
        api.POST('/api/v1/workspaces/{workspaceId}/findings/{findingId}/false-positive-reports', {
          params: { path: { workspaceId: ws, findingId } },
          body,
        }),
      ),
  });
}

export function useRateRemediation(ws: string, findingId: string) {
  return useMutation({
    mutationFn: (body: { rating: 'helpful' | 'not_helpful'; reason?: string; note?: string }) =>
      call<{ rating: string; reason: string | null }>(
        api.POST('/api/v1/workspaces/{workspaceId}/findings/{findingId}/remediation/rating', {
          params: { path: { workspaceId: ws, findingId } },
          body: body as never,
        }),
      ),
  });
}

/** "Copy prompt": records the template version (FR-FIX-03) and returns the Markdown. */
/** FR-REM-01: the only way an AI analysis starts. */
export function useGenerateRemediation(ws: string, findingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (language: Locale) =>
      call(
        api.POST('/api/v1/workspaces/{workspaceId}/findings/{findingId}/remediation/generate', {
          params: { path: { workspaceId: ws, findingId } },
          body: { language },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.findings(ws) }),
  });
}

/** FR-REM-10: saved analyses with their prompts, newest first. */
export function useRemediationVersions(
  ws: string,
  findingId: string,
  language: Locale,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...qk.finding(ws, findingId, language), 'versions'],
    enabled,
    queryFn: () =>
      call<{ items: RemediationVersion[] }>(
        api.GET('/api/v1/workspaces/{workspaceId}/findings/{findingId}/remediation/versions', {
          params: { path: { workspaceId: ws, findingId }, query: { language } },
        }),
      ),
  });
}

export function useCopyFixPrompt(ws: string, findingId: string) {
  return useMutation({
    mutationFn: (language: Locale) =>
      call<FixPrompt>(
        api.POST('/api/v1/workspaces/{workspaceId}/findings/{findingId}/fix-prompt/copy', {
          params: { path: { workspaceId: ws, findingId } },
          body: { language },
        }),
      ),
  });
}

export function useCombinedPrompt(ws: string) {
  return useMutation({
    mutationFn: (body: { findingIds: string[]; language: Locale }) =>
      call<CombinedPrompt>(
        api.POST('/api/v1/workspaces/{workspaceId}/fix-prompts/combined', {
          params: wsPath(ws),
          body: body as never,
        }),
      ),
  });
}

/* ------------------------------------------------------- workspace admin */

export function useEolNotices(ws: string, projectId?: string) {
  return useQuery({
    queryKey: qk.eol(ws, projectId),
    queryFn: () =>
      call<{ notices: EolNotice[]; all: EolNotice[] }>(
        api.GET('/api/v1/workspaces/{workspaceId}/eol-notices', {
          params: { path: { workspaceId: ws }, query: projectId ? { projectId } : {} },
        }),
      ),
  });
}

/** FR-ADV-04a: saved upgrade prompts, newest first. */
export function useEolPrompts(ws: string, projectId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...qk.eolPrompts(ws), projectId ?? 'all'],
    enabled,
    queryFn: () =>
      call<{ items: EolPromptVersion[] }>(
        api.GET('/api/v1/workspaces/{workspaceId}/eol-prompts', {
          params: { path: { workspaceId: ws }, query: projectId ? { projectId } : {} },
        }),
      ),
  });
}

export function useGenerateEolPrompt(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noticeId, language }: { noticeId: string; language: Locale }) =>
      call<EolPromptVersion>(
        api.POST('/api/v1/workspaces/{workspaceId}/eol-notices/{noticeId}/prompt', {
          params: { path: { workspaceId: ws, noticeId } },
          body: { language },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.eolPrompts(ws) }),
  });
}

/** FR-PRJ-04: the stack generated from the project's assets. */
export function useProjectStack(ws: string, projectId: string) {
  return useQuery({
    queryKey: qk.projectStack(ws, projectId),
    queryFn: () =>
      call<{ items: ProjectStackItem[] }>(
        api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}/stack', {
          params: { path: { workspaceId: ws, projectId } },
        }),
      ),
  });
}

/** FR-SRV-01: which asset runs on which server. */
export function useDeployments(ws: string, projectId: string) {
  return useQuery({
    queryKey: qk.deployments(ws, projectId),
    queryFn: () =>
      call<{ items: Deployment[] }>(
        api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}/deployments', {
          params: { path: { workspaceId: ws, projectId } },
        }),
      ),
  });
}

export function usePutDeployment(ws: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assetId,
      serverAssetId,
      ports,
      domains,
    }: {
      assetId: string;
      serverAssetId: string;
      ports: number[];
      domains: string[];
    }) =>
      call<Deployment>(
        api.PUT('/api/v1/workspaces/{workspaceId}/assets/{assetId}/deployments/{serverAssetId}', {
          params: { path: { workspaceId: ws, assetId, serverAssetId } },
          body: { ports, domains },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.deployments(ws, projectId) }),
  });
}

export function useDeleteDeployment(ws: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, serverAssetId }: { assetId: string; serverAssetId: string }) =>
      call(
        api.DELETE(
          '/api/v1/workspaces/{workspaceId}/assets/{assetId}/deployments/{serverAssetId}',
          { params: { path: { workspaceId: ws, assetId, serverAssetId } } },
        ),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.deployments(ws, projectId) }),
  });
}

/** FR-PRJ-04: versions to offer for a stack picker value; they change at most daily. */
export function useStackVersions(ws: string, name: string) {
  return useQuery({
    queryKey: ['ws', ws, 'stack-versions', name] as const,
    queryFn: () =>
      call<StackVersions>(
        api.GET('/api/v1/workspaces/{workspaceId}/stack-versions', {
          params: { path: { workspaceId: ws }, query: { name: name as never } },
        }),
      ),
    enabled: name !== '',
    staleTime: 3_600_000,
  });
}

/** FR-PRI-04: a manual P1 moves priorities, due dates and the counts on projects and assets. */
function useInvalidateAfterOverride(ws: string) {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({
      predicate: (q) => {
        const [root, id, area] = q.queryKey;
        return (
          root === 'ws' &&
          id === ws &&
          (area === 'findings' ||
            area === 'advisory-overrides' ||
            area === 'projects' ||
            area === 'project')
        );
      },
    });
}

export function useAdvisoryOverrides(ws: string) {
  return useQuery({
    queryKey: qk.advisoryOverrides(ws),
    queryFn: () =>
      call<AdvisoryOverrideList>(
        api.GET('/api/v1/workspaces/{workspaceId}/advisory-priority-overrides', {
          params: wsPath(ws),
        }),
      ),
  });
}

export function useSetAdvisoryOverride(ws: string) {
  const invalidate = useInvalidateAfterOverride(ws);
  return useMutation({
    mutationFn: ({ groupId, reason }: { groupId: string; reason: string }) =>
      call<AdvisoryOverride>(
        api.PUT('/api/v1/workspaces/{workspaceId}/advisory-priority-overrides/{groupId}', {
          params: { path: { workspaceId: ws, groupId } },
          body: { reason },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveAdvisoryOverride(ws: string) {
  const invalidate = useInvalidateAfterOverride(ws);
  return useMutation({
    mutationFn: (groupId: string) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/advisory-priority-overrides/{groupId}', {
          params: { path: { workspaceId: ws, groupId } },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useSlaPolicy(ws: string) {
  return useQuery({
    queryKey: qk.sla(ws),
    queryFn: () =>
      call<SlaPolicy>(
        api.GET('/api/v1/workspaces/{workspaceId}/sla-policy', { params: wsPath(ws) }),
      ),
  });
}

export function useUpdateSlaPolicy(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SlaPolicy) =>
      call<SlaPolicy>(
        api.PUT('/api/v1/workspaces/{workspaceId}/sla-policy', { params: wsPath(ws), body }),
      ),
    onSuccess: async (data) => {
      qc.setQueryData(qk.sla(ws), data);
      await qc.invalidateQueries({ queryKey: qk.findings(ws) });
    },
  });
}

export function useApiTokens(ws: string) {
  return useQuery({
    queryKey: qk.tokens(ws),
    queryFn: () =>
      call<List<ApiToken>>(
        api.GET('/api/v1/workspaces/{workspaceId}/api-tokens', { params: wsPath(ws) }),
      ),
  });
}

/** A new or revoked asset token changes the asset's CI state (`ci`) on its page and list. */
const invalidateTokens = (qc: QueryClient, ws: string) =>
  Promise.all([
    qc.invalidateQueries({ queryKey: qk.tokens(ws) }),
    qc.invalidateQueries({
      predicate: (q) =>
        q.queryKey[1] === ws && (q.queryKey[2] === 'asset' || q.queryKey[4] === 'assets'),
    }),
  ]);

export function useCreateApiToken(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      kind: ApiToken['kind'];
      projectId?: string;
      projectIds?: string[];
      assetId?: string;
      scopes?: string[];
      expiresInDays?: number;
    }) =>
      call<CreatedApiToken>(
        api.POST('/api/v1/workspaces/{workspaceId}/api-tokens', {
          params: wsPath(ws),
          body: body as never,
        }),
      ),
    onSuccess: () => invalidateTokens(qc, ws),
  });
}

export function useRenameApiToken(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tokenId, name }: { tokenId: string; name: string }) =>
      call<ApiToken>(
        api.PATCH('/api/v1/workspaces/{workspaceId}/api-tokens/{tokenId}', {
          params: { path: { workspaceId: ws, tokenId } },
          body: { name },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.tokens(ws) }),
  });
}

export function useRevokeApiToken(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/api-tokens/{tokenId}', {
          params: { path: { workspaceId: ws, tokenId } },
          body: {},
        }),
      ),
    onSuccess: () => invalidateTokens(qc, ws),
  });
}

export function useAuditEvents(ws: string) {
  return useInfiniteQuery({
    queryKey: qk.audit(ws),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call<AuditPage>(
        api.GET('/api/v1/workspaces/{workspaceId}/audit-events', {
          params: {
            path: { workspaceId: ws },
            query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) } as never,
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** Per-agent MCP setup snippets for a project (FR-FIX-02, FR-MCP-08). */
export function useMcpSetup(ws: string, projectId: string, assetId: string | null) {
  return useQuery({
    queryKey: ['ws', ws, 'project', projectId, 'mcp-setup', assetId ?? ''],
    queryFn: () =>
      call<McpSetup>(
        api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}/mcp-setup', {
          params: {
            path: { workspaceId: ws, projectId },
            query: assetId ? { assetId } : {},
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/* ------------------------------------------------- assignments (FR-PRJ-05) */

export type AssignmentTarget = 'client' | 'project';

export function useAssignments(ws: string, kind: AssignmentTarget, id: string) {
  return useQuery({
    queryKey: qk.assignments(ws, kind, id),
    queryFn: () =>
      kind === 'client'
        ? call<List<Assignment>>(
            api.GET('/api/v1/workspaces/{workspaceId}/clients/{clientId}/assignments', {
              params: { path: { workspaceId: ws, clientId: id } },
            }),
          )
        : call<List<Assignment>>(
            api.GET('/api/v1/workspaces/{workspaceId}/projects/{projectId}/assignments', {
              params: { path: { workspaceId: ws, projectId: id } },
            }),
          ),
  });
}

export function useSetAssignment(ws: string, kind: AssignmentTarget, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, assigned }: { membershipId: string; assigned: boolean }) => {
      if (kind === 'client') {
        const params = { path: { workspaceId: ws, clientId: id, membershipId } };
        const path =
          '/api/v1/workspaces/{workspaceId}/clients/{clientId}/assignments/{membershipId}';
        return call<unknown>(assigned ? api.PUT(path, { params }) : api.DELETE(path, { params }));
      }
      const params = { path: { workspaceId: ws, projectId: id, membershipId } };
      const path =
        '/api/v1/workspaces/{workspaceId}/projects/{projectId}/assignments/{membershipId}';
      return call<unknown>(assigned ? api.PUT(path, { params }) : api.DELETE(path, { params }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.assignments(ws, kind, id) }),
  });
}

/* --------------------------------------------- notifications (§5.10, M5) */

export function useMyChannels() {
  return useQuery({
    queryKey: qk.myChannels,
    queryFn: () => call<MyChannels>(api.GET('/api/v1/me/notification-channels')),
  });
}

/** One-time `t.me/<bot>?start=<token>` link (10 minutes, FR-NOT-11). */
export function useTelegramLink() {
  return useMutation({
    mutationFn: () =>
      call<TelegramLink>(api.POST('/api/v1/me/notification-channels/telegram/link')),
  });
}

/** LINE account link: exchanges the bot's link token for LINE's redirect URL. */
export function useLineAccountLink() {
  return useMutation({
    mutationFn: (linkToken: string) =>
      call<LineAccountLink>(
        api.POST('/api/v1/me/notification-channels/line/account-link', { body: { linkToken } }),
      ),
  });
}

export function useUnlinkChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: 'line' | 'telegram') =>
      call<unknown>(
        api.DELETE('/api/v1/me/notification-channels/{kind}', { params: { path: { kind } } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.myChannels }),
  });
}

export function useNotificationPreferences(ws: string) {
  return useQuery({
    queryKey: [...qk.notifications(ws), 'preferences'],
    queryFn: () =>
      call<NotificationPreferences>(
        api.GET('/api/v1/workspaces/{workspaceId}/notifications/preferences', {
          params: wsPath(ws),
        }),
      ),
  });
}

export function usePutPreferences(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PreferencesInput) =>
      call<NotificationPreferences>(
        api.PUT('/api/v1/workspaces/{workspaceId}/notifications/preferences', {
          params: wsPath(ws),
          body,
        }),
      ),
    onSuccess: (data) => qc.setQueryData([...qk.notifications(ws), 'preferences'], data),
  });
}

export function useNotificationSettings(ws: string, enabled = true) {
  return useQuery({
    queryKey: [...qk.notifications(ws), 'settings'],
    queryFn: () =>
      call<NotificationSettings>(
        api.GET('/api/v1/workspaces/{workspaceId}/notifications/settings', {
          params: wsPath(ws),
        }),
      ),
    enabled,
  });
}

export function usePutNotificationSettings(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkspaceDefaultsInput) =>
      call<NotificationSettings>(
        api.PUT('/api/v1/workspaces/{workspaceId}/notifications/settings', {
          params: wsPath(ws),
          body,
        }),
      ),
    onSuccess: async (data) => {
      qc.setQueryData([...qk.notifications(ws), 'settings'], data);
      // Defaults change what "inherited" means in everyone's preferences.
      await qc.invalidateQueries({ queryKey: [...qk.notifications(ws), 'preferences'] });
    },
  });
}

export function useGroupChannels(ws: string, enabled = true) {
  return useQuery({
    queryKey: [...qk.notifications(ws), 'channels'],
    queryFn: () =>
      call<List<GroupChannel>>(
        api.GET('/api/v1/workspaces/{workspaceId}/notifications/channels', {
          params: wsPath(ws),
        }),
      ),
    enabled,
  });
}

/** One-time group token: `/link@<bot> <token>` in the Telegram group (Admin). */
export function useGroupLink(ws: string) {
  return useMutation({
    mutationFn: () =>
      call<GroupLink>(
        api.POST('/api/v1/workspaces/{workspaceId}/notifications/channels/telegram-group/link', {
          params: wsPath(ws),
        }),
      ),
  });
}

export function useUnlinkGroupChannel(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/notifications/channels/{channelId}', {
          params: { path: { workspaceId: ws, channelId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications(ws) }),
  });
}

export function useRoutes(ws: string, enabled = true) {
  return useQuery({
    queryKey: [...qk.notifications(ws), 'routes'],
    queryFn: () =>
      call<Routes>(
        api.GET('/api/v1/workspaces/{workspaceId}/notifications/routes', { params: wsPath(ws) }),
      ),
    enabled,
  });
}

export interface RouteInput {
  clientId: string | null;
  channelId: string;
  minPriority: 'P1' | 'P2' | 'P3' | 'P4';
  categories: string[];
}

export function usePutRoutes(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (routes: RouteInput[]) =>
      call<Routes>(
        api.PUT('/api/v1/workspaces/{workspaceId}/notifications/routes', {
          params: wsPath(ws),
          body: { routes } as never,
        }),
      ),
    onSuccess: (data) => qc.setQueryData([...qk.notifications(ws), 'routes'], data),
  });
}

export interface DeliveryFilters {
  status?: 'queued' | 'sent' | 'failed' | 'skipped';
  channel?: 'email' | 'line' | 'telegram';
}

export function useDeliveries(ws: string, filters: DeliveryFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...qk.notifications(ws), 'deliveries', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call<DeliveryPage>(
        api.GET('/api/v1/workspaces/{workspaceId}/notifications/deliveries', {
          params: {
            path: { workspaceId: ws },
            query: { limit: 50, ...filters, ...(pageParam ? { cursor: pageParam } : {}) },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

/** What an email unsubscribe link refers to (works signed out, FR-NOT-07). */
export function useUnsubscribeInfo(token: string) {
  return useQuery({
    queryKey: ['unsubscribe', token],
    queryFn: () =>
      call<UnsubscribeInfo>(
        api.GET('/api/v1/notifications/unsubscribe', { params: { query: { token } } }),
      ),
    enabled: token.length >= 10,
    retry: false,
  });
}

export function useUnsubscribe() {
  return useMutation({
    mutationFn: (token: string) =>
      call<Unsubscribed>(
        api.POST('/api/v1/notifications/unsubscribe', { params: { query: { token } } }),
      ),
  });
}

/* ------------------------------------------- plans and billing (§5.13, M7) */

/** Public catalog: plans, limits and prices from Aircraft Pay (FR-BIL-04; never hard-coded). */
export function usePlanCatalog() {
  return useQuery({
    queryKey: qk.plans,
    queryFn: () => call<PlanCatalog>(api.GET('/api/v1/billing/plans')),
    staleTime: 5 * 60_000,
  });
}

/**
 * FR-BIL-16: the signed-in account's plan and billing (it covers every workspace the account is
 * the billing owner of). `poll` refetches while a payment is being confirmed.
 */
export function useBilling(options: { enabled?: boolean; poll?: number | false } = {}) {
  return useQuery({
    queryKey: qk.billing,
    queryFn: () => call<BillingOverview>(api.GET('/api/v1/me/billing')),
    enabled: options.enabled ?? true,
    refetchInterval: options.poll ?? false,
  });
}

/**
 * Outcome of one of the account's orders, for the checkout return page: `pending` until the
 * payment is settled, then `succeeded`, `failed` (declined) or `canceled`. `poll` refetches.
 */
export function useBillingOrder(
  orderId: string,
  options: { enabled?: boolean; poll?: number | false } = {},
) {
  return useQuery({
    queryKey: [...qk.billing, 'order', orderId],
    queryFn: () =>
      call<BillingOrderStatus>(
        api.GET('/api/v1/me/billing/orders/{orderId}', { params: { path: { orderId } } }),
      ),
    enabled: (options.enabled ?? true) && Boolean(orderId),
    refetchInterval: options.poll ?? false,
    retry: false,
  });
}

/** The workspace's plan (its billing owner's), limits, usage and features (FR-BIL-01/16). */
export function useWorkspacePlan(ws: string, enabled = true) {
  return useQuery({
    queryKey: qk.workspacePlan(ws),
    queryFn: () =>
      call<WorkspacePlan>(api.GET('/api/v1/workspaces/{workspaceId}/plan', { params: wsPath(ws) })),
    enabled: enabled && Boolean(ws),
    staleTime: 60_000,
  });
}

export function useBillingPayments(enabled = true) {
  return useQuery({
    queryKey: [...qk.billing, 'payments'],
    queryFn: () =>
      call<BillingPayments>(
        api.GET('/api/v1/me/billing/payments', { params: { query: { limit: 50 } } }),
      ),
    enabled,
  });
}

export function useUpdateBillingDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BillingDetailsInput) =>
      call<BillingOverview>(api.PUT('/api/v1/me/billing/details', { body })),
    onSuccess: (data) => qc.setQueryData(qk.billing, data),
  });
}

/** Prorated upgrade amount after credit (FR-BIL-06), shown before the user confirms. */
export function useUpgradeQuote(planCode: string | null) {
  return useQuery({
    queryKey: [...qk.billing, 'quote', planCode],
    queryFn: () =>
      call<UpgradeQuote>(
        api.GET('/api/v1/me/billing/upgrade-quote', {
          params: { query: { planCode: planCode ?? '' } },
        }),
      ),
    enabled: Boolean(planCode),
    staleTime: 0,
  });
}

export type PlanChangeAction = 'checkout' | 'upgrade';

/** A plan change reaches every workspace the account pays for: their plan views refetch. */
function invalidatePlans(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: qk.billing }),
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'ws' && q.queryKey[2] === 'plan' }),
  ]);
}

/** First purchase (checkout) or upgrade: the result says where to go next. */
export function useStartPlanChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      action,
      planCode,
      locale,
      acceptedTerms,
    }: {
      action: PlanChangeAction;
      planCode: string;
      locale: Locale;
      /** The buyer ticked the terms and refund consent (§5.13). */
      acceptedTerms: true;
    }) =>
      call<BillingOrder>(
        action === 'checkout'
          ? api.POST('/api/v1/me/billing/checkout', { body: { planCode, locale, acceptedTerms } })
          : api.POST('/api/v1/me/billing/upgrade', { body: { planCode, locale, acceptedTerms } }),
      ),
    onSettled: () => invalidatePlans(qc),
  });
}

/** Downgrade (or Free) at the end of the period; `null` keeps the current plan. */
export function useScheduleDowngrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planCode: string | null) =>
      call<BillingOverview>(
        planCode
          ? api.POST('/api/v1/me/billing/downgrade', { body: { planCode } })
          : api.DELETE('/api/v1/me/billing/downgrade'),
      ),
    onSuccess: (data) => qc.setQueryData(qk.billing, data),
    onSettled: () => invalidatePlans(qc),
  });
}

/** Save or replace the card (checkout in setup mode). */
export function useReplaceCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locale: Locale) =>
      call<BillingOrder>(api.POST('/api/v1/me/billing/payment-method', { body: { locale } })),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.billing }),
  });
}

/** FR-BIL-16: hand the workspace's billing to another Owner (the billing owner only). */
export function useTransferBilling(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { membershipId: string; reason?: string }) =>
      call<Workspace>(
        api.PUT('/api/v1/workspaces/{workspaceId}/billing-owner', { params: wsPath(ws), body }),
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: qk.workspaces }),
        qc.invalidateQueries({ queryKey: qk.ws(ws), exact: true }),
        invalidatePlans(qc),
      ]),
  });
}

/* ---------------------------------------------- reports (FR-RPT-02, 05) */

export function useReports(ws: string, enabled = true) {
  return useQuery({
    queryKey: qk.reports(ws),
    queryFn: () =>
      call<List<Report>>(
        api.GET('/api/v1/workspaces/{workspaceId}/reports', {
          params: { path: { workspaceId: ws }, query: { limit: 50 } },
        }),
      ),
    enabled,
    // Poll while a job is queued or running.
    refetchInterval: (q) =>
      q.state.data?.items.some((r) => r.status === 'queued' || r.status === 'running')
        ? 2000
        : false,
  });
}

export function useCreateReport(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReportInput) =>
      call<Report>(
        api.POST('/api/v1/workspaces/{workspaceId}/reports', { params: wsPath(ws), body }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.reports(ws) }),
  });
}

export function useCreateExport(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExportInput) =>
      call<Report>(
        api.POST('/api/v1/workspaces/{workspaceId}/exports', { params: wsPath(ws), body }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.reports(ws) }),
  });
}

/* --------------------------------------- GitHub App (FR-INV-05, 06, M7) */

export function useGithubInstallations(ws: string, enabled = true) {
  return useQuery({
    queryKey: [...qk.github(ws), 'installations'],
    queryFn: () =>
      call<GithubInstallations>(
        api.GET('/api/v1/workspaces/{workspaceId}/integrations/github/installations', {
          params: wsPath(ws),
        }),
      ),
    enabled,
  });
}

/** Installation URL with a signed state (1 h); the browser then goes to GitHub. */
export function useGithubInstall(ws: string) {
  return useMutation({
    mutationFn: () =>
      call<{ url: string }>(
        api.POST('/api/v1/workspaces/{workspaceId}/integrations/github/install', {
          params: wsPath(ws),
        }),
      ),
  });
}

export function useGithubAvailableRepos(ws: string, installationId: string | null) {
  return useQuery({
    queryKey: [...qk.github(ws), 'available', installationId],
    queryFn: () =>
      call<List<GithubAvailableRepo>>(
        api.GET(
          '/api/v1/workspaces/{workspaceId}/integrations/github/installations/{installationId}/repositories',
          { params: { path: { workspaceId: ws, installationId: installationId ?? '' } } },
        ),
      ),
    enabled: Boolean(installationId),
  });
}

export function useGithubRepositories(ws: string, enabled = true) {
  return useQuery({
    queryKey: [...qk.github(ws), 'repositories'],
    queryFn: () =>
      call<List<GithubRepository>>(
        api.GET('/api/v1/workspaces/{workspaceId}/integrations/github/repositories', {
          params: wsPath(ws),
        }),
      ),
    enabled,
  });
}

export function useTrackRepository(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      installationId: string;
      repoId: number;
      projectId: string;
      name?: string;
    }) =>
      call<GithubRepository>(
        api.POST('/api/v1/workspaces/{workspaceId}/integrations/github/repositories', {
          params: wsPath(ws),
          body,
        }),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.github(ws) });
      await qc.invalidateQueries({ queryKey: qk.projects(ws) });
    },
  });
}

export function useDiscoverManifests(ws: string, assetId: string) {
  return useMutation({
    mutationFn: (branch: string) =>
      call<GithubDiscovery>(
        api.POST(
          '/api/v1/workspaces/{workspaceId}/integrations/github/repositories/{assetId}/discover',
          { params: { path: { workspaceId: ws, assetId } }, body: { branch } },
        ),
      ),
  });
}

export function useConfigureRepository(ws: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { branch: string; manifestPaths: string[] }) =>
      call<GithubRepository>(
        api.PATCH('/api/v1/workspaces/{workspaceId}/integrations/github/repositories/{assetId}', {
          params: { path: { workspaceId: ws, assetId } },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.github(ws) }),
  });
}

export function useScanRepository(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) =>
      call<{ status: 'accepted' }>(
        api.POST(
          '/api/v1/workspaces/{workspaceId}/integrations/github/repositories/{assetId}/scan',
          { params: { path: { workspaceId: ws, assetId } } },
        ),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.github(ws) }),
  });
}

export function useUntrackRepository(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) =>
      call<unknown>(
        api.DELETE('/api/v1/workspaces/{workspaceId}/integrations/github/repositories/{assetId}', {
          params: { path: { workspaceId: ws, assetId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.github(ws) }),
  });
}

/* ------------------------------------------ staff console (FR-ADM-02..04) */

/** The caller's staff role; fails with STAFF_ONLY (or 2FA required) for everyone else. */
export function useStaffUsers() {
  return useQuery({
    queryKey: [...qk.staff, 'users'],
    queryFn: () => call<List<StaffUser>>(api.GET('/api/v1/staff/users')),
  });
}

export function useSetStaffUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: StaffRole | null }) =>
      call<List<StaffUser>>(api.PUT('/api/v1/staff/users', { body })),
    onSuccess: (data) => qc.setQueryData([...qk.staff, 'users'], data),
  });
}

export function useSyncHealth() {
  return useQuery({
    queryKey: [...qk.staff, 'sync-health'],
    queryFn: () => call<SyncHealth>(api.GET('/api/v1/staff/sync-health')),
    refetchInterval: 60_000,
  });
}

export function useFalsePositives(state: FalsePositiveState) {
  return useQuery({
    queryKey: [...qk.staff, 'false-positives', state],
    queryFn: () =>
      call<List<FalsePositiveReport>>(
        api.GET('/api/v1/staff/false-positives', { params: { query: { state, limit: 100 } } }),
      ),
  });
}

export function useReviewFalsePositive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      workspaceId: string;
      decision: 'accepted' | 'rejected';
      reason: string;
    }) =>
      call<unknown>(
        api.POST('/api/v1/staff/false-positives/{id}/review', {
          params: { path: { id } },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...qk.staff, 'false-positives'] }),
  });
}

export function useMitigationReviews() {
  return useQuery({
    queryKey: [...qk.staff, 'mitigation-reviews'],
    queryFn: () =>
      call<MitigationReviewQueue>(
        api.GET('/api/v1/staff/mitigation-reviews', { params: { query: { limit: 100 } } }),
      ),
  });
}

export function useMitigationReview(intelId: string) {
  return useQuery({
    queryKey: [...qk.staff, 'mitigation-reviews', intelId],
    queryFn: () =>
      call<MitigationReviewDetail>(
        api.GET('/api/v1/staff/mitigation-reviews/{intelId}', {
          params: { path: { intelId } },
        }),
      ),
  });
}

export function useDecideMitigationReview(intelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { action: 'approve' | 'hide'; reason: string; hiddenItemIds?: string[] }) =>
      call<unknown>(
        api.POST('/api/v1/staff/mitigation-reviews/{intelId}', {
          params: { path: { intelId } },
          body,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...qk.staff, 'mitigation-reviews'] }),
  });
}

export function useAiUsage(range: { from?: string; to?: string }) {
  return useQuery({
    queryKey: [...qk.staff, 'ai-usage', range],
    queryFn: () =>
      call<AiUsage>(
        api.GET('/api/v1/staff/ai-usage', {
          params: {
            query: {
              ...(range.from ? { from: range.from } : {}),
              ...(range.to ? { to: range.to } : {}),
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useStaffWorkspaces(query: string) {
  return useQuery({
    queryKey: [...qk.staff, 'workspaces', query],
    queryFn: () =>
      call<List<StaffWorkspace>>(
        api.GET('/api/v1/staff/workspaces', {
          params: { query: { ...(query ? { query } : {}), limit: 100 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** The caller's active read-only access grant to a workspace, if any (FR-ADM-03). */
export function useStaffAccess(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: [...qk.staff, 'access', workspaceId],
    queryFn: () =>
      call<{ grant: StaffGrant | null }>(
        api.GET('/api/v1/staff/workspaces/{workspaceId}/access', {
          params: { path: { workspaceId } },
        }),
      ),
    enabled,
    retry: false,
  });
}

export function useStaffGrant(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string; minutes?: number } | null) =>
      call<unknown>(
        body
          ? api.POST('/api/v1/staff/workspaces/{workspaceId}/access', {
              params: { path: { workspaceId } },
              body,
            })
          : api.DELETE('/api/v1/staff/workspaces/{workspaceId}/access', {
              params: { path: { workspaceId } },
            }),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [...qk.staff, 'access', workspaceId] });
      await qc.invalidateQueries({ queryKey: [...qk.staff, 'workspace', workspaceId] });
      // Pages of the workspace read through the grant (or no longer can).
      qc.removeQueries({ queryKey: qk.ws(workspaceId) });
    },
  });
}

export function useStaffWorkspace(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...qk.staff, 'workspace', workspaceId],
    queryFn: () =>
      call<StaffWorkspaceDetail>(
        api.GET('/api/v1/staff/workspaces/{workspaceId}', { params: { path: { workspaceId } } }),
      ),
    enabled,
    retry: false,
  });
}

export function useStaffPayments(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...qk.staff, 'workspace', workspaceId, 'payments'],
    queryFn: () =>
      call<List<StaffPayment>>(
        api.GET('/api/v1/staff/workspaces/{workspaceId}/payments', {
          params: { path: { workspaceId } },
        }),
      ),
    enabled,
    retry: false,
  });
}

function useStaffWorkspaceMutation<V, R = unknown>(workspaceId: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [...qk.staff, 'workspace', workspaceId] });
      await qc.invalidateQueries({ queryKey: [...qk.staff, 'workspaces'] });
    },
  });
}

export function usePlanOverride(workspaceId: string) {
  return useStaffWorkspaceMutation(workspaceId, (body: PlanOverrideInput) =>
    call<unknown>(
      api.PUT('/api/v1/staff/workspaces/{workspaceId}/plan-override', {
        params: { path: { workspaceId } },
        body,
      }),
    ),
  );
}

export function useRemovePlanOverride(workspaceId: string) {
  return useStaffWorkspaceMutation(workspaceId, (reason: string) =>
    call<unknown>(
      api.DELETE('/api/v1/staff/workspaces/{workspaceId}/plan-override', {
        params: { path: { workspaceId } },
        body: { reason },
      }),
    ),
  );
}

export function useAdjustCredit(workspaceId: string) {
  return useStaffWorkspaceMutation(workspaceId, (body: { amount: number; reason: string }) =>
    call<{ creditBalance: number }>(
      api.POST('/api/v1/staff/workspaces/{workspaceId}/credit', {
        params: { path: { workspaceId } },
        body,
      }),
    ),
  );
}

export function useRefund(workspaceId: string) {
  return useStaffWorkspaceMutation(
    workspaceId,
    ({ paymentId, ...body }: { paymentId: string; amount?: number; reason: string }) =>
      call<StaffRefund>(
        api.POST('/api/v1/staff/workspaces/{workspaceId}/payments/{paymentId}/refunds', {
          params: { path: { workspaceId, paymentId } },
          body,
        }),
      ),
  );
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: [...qk.staff, 'flags'],
    queryFn: () => call<List<FeatureFlag>>(api.GET('/api/v1/staff/feature-flags')),
  });
}

export function useSetFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      ...body
    }: {
      key: string;
      enabled: boolean;
      workspaceIds?: string[];
      description?: string | null;
    }) =>
      call<FeatureFlag>(
        api.PUT('/api/v1/staff/feature-flags/{key}', { params: { path: { key } }, body }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...qk.staff, 'flags'] }),
  });
}

export function useDeleteFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      call<unknown>(api.DELETE('/api/v1/staff/feature-flags/{key}', { params: { path: { key } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...qk.staff, 'flags'] }),
  });
}

export function useStaffServiceSwitches() {
  return useQuery({
    queryKey: [...qk.staff, 'service-switches'],
    queryFn: () => call<ServiceSwitches>(api.GET('/api/v1/staff/service-switches')),
  });
}

export function useUpdateServiceSwitches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ServiceSwitches>) =>
      call<ServiceSwitches>(api.PUT('/api/v1/staff/service-switches', { body })),
    onSuccess: (data) => {
      qc.setQueryData([...qk.staff, 'service-switches'], data);
      qc.setQueryData(qk.serviceStatus, data);
    },
  });
}

export function useStaffAlerts(resolved: boolean) {
  return useQuery({
    queryKey: [...qk.staff, 'alerts', resolved],
    queryFn: () =>
      call<List<StaffAlert>>(
        api.GET('/api/v1/staff/alerts', {
          params: { query: { resolved: resolved ? 'true' : 'false', limit: 100 } },
        }),
      ),
  });
}

export function useResolveAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      call<unknown>(api.POST('/api/v1/staff/alerts/{id}/resolve', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...qk.staff, 'alerts'] }),
  });
}

/* ------------------------------------------------ feedback (FR-ACC-13, FR-ADM-05) */

export function useMyFeedback() {
  return useQuery({
    queryKey: qk.myFeedback,
    queryFn: () => call<List<MyFeedback>>(api.GET('/api/v1/me/feedback')),
  });
}

export function useSendFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFeedback) =>
      call<MyFeedback>(api.POST('/api/v1/me/feedback', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.myFeedback }),
  });
}

export function useStaffFeedback(status: StaffFeedbackFilter, kind?: FeedbackKind, limit = 100) {
  return useQuery({
    queryKey: [...qk.staff, 'feedback', status, kind ?? null, limit],
    queryFn: () =>
      call<StaffFeedbackList>(
        api.GET('/api/v1/staff/feedback', {
          params: { query: { status, limit, ...(kind ? { kind } : {}) } },
        }),
      ),
  });
}

export function useStaffFeedbackItem(id: string) {
  return useQuery({
    queryKey: [...qk.staff, 'feedback', 'item', id],
    queryFn: () =>
      call<StaffFeedback>(api.GET('/api/v1/staff/feedback/{id}', { params: { path: { id } } })),
  });
}

export function useUpdateFeedback(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { status?: FeedbackStatus; reply?: string | null }) =>
      call<StaffFeedback>(
        api.PUT('/api/v1/staff/feedback/{id}', { params: { path: { id } }, body }),
      ),
    onSuccess: (data) => {
      qc.setQueryData([...qk.staff, 'feedback', 'item', id], data);
      return qc.invalidateQueries({ queryKey: [...qk.staff, 'feedback'] });
    },
  });
}
